/* Packrift local fit tools. No network, prices, cart writes, or storage. */
const numeric = (value, name, allowZero = false) => {
  if (value === null || value === undefined || typeof value === 'boolean' || String(value).trim() === '') throw new Error(`Enter ${name}.`);
  const n = Number(value);
  if (!Number.isFinite(n) || (allowZero ? n < 0 : n <= 0)) throw new Error(`${name} must be ${allowZero ? 'zero or greater' : 'greater than zero'}.`);
  return n;
};
export function estimateMailer({ width, length, thickness, allowance }) {
  const w = numeric(width, 'packed width');
  const l = numeric(length, 'packed length');
  const t = numeric(thickness, 'packed thickness', true);
  const a = numeric(allowance, 'extra allowance', true);
  const opening = w + t + a;
  const pocket = l + t + a;
  if (![opening, pocket].every(Number.isFinite)) throw new Error('The measurements are too large to calculate.');
  return { opening, pocket, allowance: a };
}
export function calculatePairOrder(pair, sets) {
  const target = numeric(sets, 'a whole number of complete sets');
  if (!Number.isSafeInteger(target)) throw new Error('Enter a whole number of complete sets within the supported range.');
  if (!pair || !pair.inner || !pair.outer) return { status: 'missing-part' };
  if (!pair.quantityVerified) return { status: 'confirm-order-unit' };
  const ic = numeric(pair.inner.count, 'inner bundle count');
  const oc = numeric(pair.outer.count, 'outer bundle count');
  if (![ic, oc].every(Number.isSafeInteger)) throw new Error('Confirm both bundle counts before calculating.');
  const innerBundles = Math.ceil(target / ic);
  const outerBundles = Math.ceil(target / oc);
  const innerPieces = innerBundles * ic;
  const outerPieces = outerBundles * oc;
  if (![innerPieces, outerPieces].every(Number.isSafeInteger)) throw new Error('The quantity is too large to calculate reliably.');
  return { status: 'calculated', target, innerBundles, outerBundles, innerPieces, outerPieces, completeSets: Math.min(innerPieces, outerPieces), spareInner: innerPieces - target, spareOuter: outerPieces - target };
}
const format = n => new Intl.NumberFormat('en-US', { maximumFractionDigits: 4 }).format(n);
// Round up to 0.01 in so the displayed planning estimate never understates the computed space.
const dimension = n => format(Math.ceil((n - Number.EPSILON * Math.max(1, n)) * 100) / 100);
const element = (doc, tag, text, className) => {
  const el = doc.createElement(tag);
  if (text !== undefined) el.textContent = text;
  if (className) el.className = className;
  return el;
};
export function initFitTools(root = document) {
  const doc = root.ownerDocument || root;
  root.querySelectorAll('[data-pxu-mailer]').forEach(section => {
    if (section.dataset.pxuInitialized) return;
    const form = section.querySelector('[data-pxu-mailer-form]');
    const result = section.querySelector('[data-pxu-mailer-result]');
    if (!form || !result) return;
    section.dataset.pxuInitialized = 'true';
    const calculate = () => {
      try {
        const values = Object.fromEntries(['width', 'length', 'thickness', 'allowance'].map(key => [key, form.elements.namedItem(key).value]));
        const estimate = estimateMailer(values);
        result.replaceChildren(
          element(doc, 'p', 'Estimated usable space to look for', 'pxu-eyebrow'),
          element(doc, 'h3', `${dimension(estimate.opening)} in opening × ${dimension(estimate.pocket)} in pocket length`),
          element(doc, 'p', `Includes your ${format(estimate.allowance)} in extra allowance in each result. Figures are rounded up to the next 0.01 in where needed.`),
          element(doc, 'p', 'Compare these requirements with the exact SKU’s manufacturer-confirmed usable dimensions. Test a packed sample and the closure. This estimate does not select or certify a numbered mailer.', 'pxu-note')
        );
      } catch (error) {
        result.replaceChildren(element(doc, 'p', error.message));
      }
    };
    form.addEventListener('submit', event => { event.preventDefault(); calculate(); });
    // Remove stale results as soon as any measurement changes.
    form.addEventListener('input', () => result.replaceChildren(element(doc, 'p', 'Measurements changed. Calculate again to update the estimate.')));
    section.querySelector('[data-pxu-mailer-example]')?.addEventListener('click', () => {
      const example = { width: 5, length: 7, thickness: 1, allowance: 0.5 };
      Object.entries(example).forEach(([key, value]) => { form.elements.namedItem(key).value = value; });
      calculate();
    });
  });
  root.querySelectorAll('[data-pxu-pairs]').forEach(section => {
    if (section.dataset.pxuInitialized) return;
    const select = section.querySelector('[data-pxu-pair-select]');
    const count = section.querySelector('[data-pxu-set-count]');
    const result = section.querySelector('[data-pxu-pair-result]');
    let pairs;
    try { pairs = JSON.parse(section.querySelector('[data-pxu-pair-data]').textContent); }
    catch { return; } // Static SKU reference remains usable when data is absent or invalid.
    if (!Array.isArray(pairs) || !select || !count || !result) return;
    section.dataset.pxuInitialized = 'true';
    const render = () => {
      const pair = pairs.find(p => p.id === select.value);
      if (!pair) { result.replaceChildren(element(doc, 'p', 'Choose a listed family.')); return; }
      const nodes = [element(doc, 'h3', pair.label), element(doc, 'p', 'Both halves are required for one complete set.')];
      const parts = element(doc, 'div', undefined, 'pxu-grid');
      for (const [key, label] of [['inner', 'INNER'], ['outer', 'OUTER']]) {
        const part = pair[key];
        const card = element(doc, 'div', undefined, 'pxu-card');
        card.append(element(doc, 'p', label, 'pxu-eyebrow'));
        if (part) {
          const link = element(doc, 'a', `View ${part.sku}`);
          if (typeof part.url === 'string' && /^\/products\/[a-z0-9-]+$/.test(part.url)) link.href = part.url;
          card.append(link, element(doc, 'p', `${format(part.count)} pieces per bundle listed`));
          if (pair.id !== 'T48638INHD' || key !== 'outer') card.append(element(doc, 'p', `Listed part size: ${part.size} in`));
        } else card.append(element(doc, 'p', 'Matching inner product is not listed. Confirm it with Packrift.'));
        parts.append(card);
      }
      nodes.push(parts);
      for (const warning of pair.warnings || []) nodes.push(element(doc, 'p', warning, 'pxu-note'));
      try {
        const order = calculatePairOrder(pair, count.value);
        if (order.status === 'calculated') {
          nodes.push(element(doc, 'h3', `For ${format(order.target)} complete set${order.target === 1 ? '' : 's'}: ${format(order.innerBundles)} inner bundle${order.innerBundles === 1 ? '' : 's'} + ${format(order.outerBundles)} outer bundle${order.outerBundles === 1 ? '' : 's'}`));
          nodes.push(element(doc, 'p', `Supplies ${format(order.innerPieces)} inner pieces and ${format(order.outerPieces)} outer pieces: enough parts for ${format(order.completeSets)} complete sets. After your requested quantity, ${format(order.spareInner)} inner and ${format(order.spareOuter)} outer pieces remain.`));
        } else nodes.push(element(doc, 'p', order.status === 'missing-part' ? 'Complete-set bundle calculation unavailable until the matching inner is confirmed.' : 'Bundle calculation paused until Packrift confirms the inner order unit.'));
      } catch (error) { nodes.push(element(doc, 'p', error.message)); }
      nodes.push(element(doc, 'p', 'Approved overlap and maximum assembled length are not specified. Confirm these for your application before ordering.', 'pxu-note'));
      result.replaceChildren(...nodes);
    };
    select.addEventListener('change', render);
    count.addEventListener('input', render);
    render();
  });
}
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initFitTools(), { once: true });
  else initFitTools();
}
